import { compact, difference, differenceBy, intersectionBy, isEmpty, isEqual, times, uniq, uniqBy } from "lodash";
import React from "react";
import Checkbox from "../../../Inputs/Checkbox/Checkbox";
import { TUseStepValue } from "../index";
import { formatRun } from "../../../../utils/Format";
import { Day } from "../../../../entities/Day";

interface Props {
  options: Array<TUseStepValue>;
  selection: Array<TUseStepValue>;
  onChange: (options: Array<TUseStepValue>) => void;
}

const isChecked = (selection: Array<TUseStepValue>, option: TUseStepValue) => {
  return selection.some(selectedOption => isEqual(selectedOption, option));
};

const stepValueIndexKey = (option: TUseStepValue) => {
  return [option.use, option.step, option.step].join("-");
}

const UseStepControl = (props: Props) => {
  const { options, selection, onChange } = props;

  const columnHeaders = uniq(options.map(option => option.use));

  const columnValues = options.reduce((acc, option) => {
    if (!acc[option.use]) {
      acc[option.use] = [];
    }

    acc[option.use].push(option);

    return acc;
  }, [] as Array<Array<TUseStepValue>>);

  const toggleColumn = (column: number) => {
    const selected = selection.filter(option => option.use === column);

    const allSelected = selected.length === columnValues[column].length;

    const newOptions = allSelected
      ? selection.filter(option => option.use !== column)
      : selection.concat(columnValues[column]);

    const options = uniqBy(newOptions, stepValueIndexKey);

    onChange(options);
  };

  const toggleRow = (week: number) => {
    const allWeekOptions = compact(columnValues.map(values => values[week]));

    const selected = intersectionBy(allWeekOptions, selection, stepValueIndexKey);

    const allSelected = selected.length === allWeekOptions.length;

    const newOptions = allSelected
      ? differenceBy(selection, allWeekOptions, stepValueIndexKey)
      : selection.concat(allWeekOptions);

    onChange(newOptions);
  };

  const onCheckboxChange = (option: TUseStepValue, isChecked: boolean) => {
    if (isChecked) {
      onChange(selection.concat(option));
    } else {
      onChange(selection.filter(selectedOption => !isEqual(selectedOption, option)));
    }
  };

  return (
    <div className="use-step-control">
      <table>
        <thead>
          <tr>
            <th className="table-heading" />
            {columnHeaders.map(header => (
              <th className="table-heading" key={header}>
                <button
                  type="button"
                  className="btn__links use-step-control__button"
                  onClick={() => toggleColumn(header)}
                >
                  <span>{formatRun(header as Day)}</span>
                </button>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {times(6, week => (
            <tr key={week}>
              <th>
                <button
                  type="button"
                  className="btn__links use-step-control__button"
                  onClick={() => toggleRow(week)}
                >
                  Week {week + 1}
                </button>
              </th>

              {columnValues.map((values, column) => {
                if (isEmpty(values)) {
                  return null;
                }

                const option = values[week];
                const label = option && option.step;
                const checked = isChecked(selection, option);

                return (
                  <td key={`${week}-${column}`}>
                    {option && (
                      <span className={`use-step-control__item ${checked && "checked"}`}>
                        <Checkbox
                          label={label}
                          value={label}
                          name={label}
                          checked={checked}
                          onChange={checked => onCheckboxChange(option, checked)}
                        />
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UseStepControl;
